import { normalizeEffectSections, type EffectSection } from './sections';

export const LUT_SIZE = 17;
export const LUT_DATA_LENGTH = 39304;
export const LUT_PRESET_IDS = ['warm', 'cool', 'cinema', 'vintage', 'vivid', 'mono'] as const;
export type LutPresetId = typeof LUT_PRESET_IDS[number];
export type VideoLut = { preset: LutPresetId | 'custom'; strength: number; name?: string; data?: string };
export const EFFECT_CONTROLS = [
  { key: 'exposure', min: -2, max: 2, step: 0.05, group: 'adjust' },
  ...(['contrast', 'saturation', 'temperature', 'tint', 'shadows', 'highlights', 'vibrance'] as const).map(key => ({ key, min: -100, max: 100, step: 1, group: 'adjust' })),
  ...(['blur', 'vignette', 'grain', 'grayscale'] as const).map(key => ({ key, min: 0, max: 100, step: 1, group: 'visual' })),
] as const;
export type EffectControlKey = typeof EFFECT_CONTROLS[number]['key'];
export type VideoEffects = { version: 1; enabled: boolean; lut: VideoLut | null } & Record<EffectControlKey, number> & { sections?: EffectSection[] };
export const DEFAULT_VIDEO_EFFECTS: VideoEffects = {
  version: 1, enabled: true, lut: null, exposure: 0, contrast: 0, saturation: 0,
  temperature: 0, tint: 0, shadows: 0, highlights: 0, vibrance: 0,
  blur: 0, vignette: 0, grain: 0, grayscale: 0,
};
const bounded = (value: unknown, min: number, max: number, fallback = 0) => typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
export function isLutData(value: unknown): value is string {
  return typeof value === 'string' && value.length === LUT_DATA_LENGTH && /^[A-Za-z0-9+/]+$/.test(value);
}
export function normalizeVideoEffects(value: unknown): VideoEffects {
  const result = { ...DEFAULT_VIDEO_EFFECTS };
  if (!value || typeof value !== 'object' || (value as Record<string, unknown>).version !== 1) return result;
  const input = value as Record<string, unknown>;
  result.enabled = input.enabled !== false;
  for (const { key, min, max } of EFFECT_CONTROLS) result[key] = bounded(input[key], min, max);
  if (input.lut && typeof input.lut === 'object') {
    const lut = input.lut as Record<string, unknown>;
    const strength = bounded(lut.strength, 0, 1, 1);
    if (lut.preset === 'custom' && isLutData(lut.data)) {
      result.lut = { preset: 'custom', strength, name: typeof lut.name === 'string' ? lut.name.slice(0, 80) : 'Custom LUT', data: lut.data };
    } else if (LUT_PRESET_IDS.includes(lut.preset as LutPresetId)) result.lut = { preset: lut.preset as LutPresetId, strength };
  }
  const sections = normalizeEffectSections(input.sections);
  if (sections.length) result.sections = sections;
  return result;
}
export function hasVideoEffects(value: VideoEffects): boolean {
  return value.enabled && (!!value.lut && value.lut.strength > 0 || EFFECT_CONTROLS.some(({ key }) => value[key] !== 0));
}
export function hasAnyVideoEffects(value: VideoEffects): boolean {
  // The switch gates only the base look (hasVideoEffects); sections always apply.
  return hasVideoEffects(value) || (value.sections ?? []).some((s) => s.intensity > 0);
}
